import type { Room } from "@/lib/api";

/**
 * One live room and the rooms that were merged into it, all as indices into
 * `household.rooms`.
 *
 * Indices rather than copies: every edit on the household screen rebuilds the
 * whole `rooms` array and `RoomCard` addresses its room by index, so anything
 * rendered here has to point back at the same positions.
 */
export interface RoomGroup {
  /** Index of the live room in `household.rooms`. */
  index: number;
  /** Indices of the rooms absorbed into it, in their original order. */
  absorbed: number[];
}

/**
 * Order rooms for display: each live room followed by the rooms absorbed into
 * it. An absorbed room whose survivor is not in the list is shown standalone
 * rather than dropped, because a dangling pointer in hand-edited data must
 * never make a room disappear from the admin's view.
 */
export function groupRooms(rooms: Room[]): RoomGroup[] {
  const liveIndexById = new Map<string, number>();
  rooms.forEach((room, index) => {
    if (!room.absorbed_into && room.ha_room_id) {
      liveIndexById.set(room.ha_room_id, index);
    }
  });

  const groups: RoomGroup[] = [];
  const groupBySurvivorIndex = new Map<number, RoomGroup>();

  rooms.forEach((room, index) => {
    const survivorIndex = room.absorbed_into ? liveIndexById.get(room.absorbed_into) : undefined;

    if (survivorIndex === undefined) {
      // Live, or absorbed into a room that is not here. Either way it stands alone.
      const existing = groupBySurvivorIndex.get(index);
      if (existing) {
        // Registered early by a room it absorbed; its own turn adds nothing.
        return;
      }
      const group: RoomGroup = { index, absorbed: [] };
      groups.push(group);
      groupBySurvivorIndex.set(index, group);
      return;
    }

    let group = groupBySurvivorIndex.get(survivorIndex);
    if (!group) {
      // The survivor appears later in the array than the room it absorbed, so
      // its group is created here, out of position. The sort below puts it back.
      group = { index: survivorIndex, absorbed: [] };
      groups.push(group);
      groupBySurvivorIndex.set(survivorIndex, group);
    }
    group.absorbed.push(index);
  });

  // Live rooms keep their original order no matter where their absorbed rooms
  // sit in the array. Each group's index is the live room's own position, so
  // sorting by it is exactly that order; absorbed indices were pushed ascending.
  return groups.sort((a, b) => a.index - b.index);
}
